import { ReactNode, Suspense, lazy, useState, useEffect, useLayoutEffect } from 'react';
import { LayoutGrid, MessageSquare, Settings, CreditCard, HelpCircle, Menu, Bell, LogOut, User, Users, Shield, BookOpen, BarChart3, Calendar, FileText, Building2, Search, History, Sparkles, Heart, UserCheck, Plus, Activity, Wallet, DollarSign, Loader2, Coins, Home, Database, Star, MessageSquareHeart, MapPin, FileSpreadsheet } from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useTranslation } from 'react-i18next';

import { ThemeToggle } from '@/components/ThemeToggle';
import { PermissionGate, AdminOnly, OrgAdminOnly } from '@/components/auth/PermissionGate';
import { NotificationBell } from '@/components/NotificationBell';
import { billingAPI } from '@/lib/api';
import { formatWalletBalanceCompact, type WalletBalance } from '@/lib/money';
import { getUserProfileImageUrl } from '@/lib/userProfile';
import { useConfig } from '@/contexts/ConfigContext';
import { RolesDropdown } from '@/components/dashboard/RolesDropdown';
import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from "@/components/ui/sheet";
import { cn } from '@/lib/utils';
import { PROPERTY_SEARCH_ASSISTANT_PATH } from '@/lib/assistantRoutes';
import { useNavigateToPropertySearch } from '@/hooks/useNavigateToPropertySearch';

const SearchResumeDialog = lazy(() =>
    import('@/components/SearchResumeDialog').then(m => ({ default: m.SearchResumeDialog })),
);

interface DashboardLayoutProps {
    children: ReactNode;
    showSidebar?: boolean;
}

export const DashboardLayout = ({ children, showSidebar = true }: DashboardLayoutProps) => {
    const location = useLocation();
    const { user, logout, activeRole } = useAuth();
    const isOwnerTenantUser = user?.role === 'owner' || user?.role === 'tenant';
    const canPostProperty =
        isOwnerTenantUser ||
        activeRole === 'org_admin' ||
        activeRole === 'org_user' ||
        activeRole === 'property_manager' ||
        user?.role === 'org_admin' ||
        user?.role === 'org_user' ||
        user?.role === 'property_manager';
    const { activeOrgId } = useOrganization();
    const { t } = useTranslation();
    const { creditsOnlyMode } = useConfig();
    const [balance, setBalance] = useState<WalletBalance | null>(null);
    const [balanceLoading, setBalanceLoading] = useState(false);

    const navigate = useNavigate();
    const {
        goToPropertySearch,
        continueSearch,
        startNewSearch,
        pendingSearch,
        showResumeDialog,
        checking: searchChecking,
    } = useNavigateToPropertySearch();

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    useEffect(() => {
        if (!user) return;
        let cancelled = false;
        const refreshBalance = () => {
            setBalanceLoading(true);
            const balancePromise = activeOrgId
                ? billingAPI.getOrgBalance(activeOrgId)
                : billingAPI.getBalance();
            balancePromise
                .then((data) => {
                    if (!cancelled) setBalance(data);
                })
                .catch(() => {
                    if (!cancelled) setBalance(null);
                })
                .finally(() => {
                    if (!cancelled) setBalanceLoading(false);
                });
        };
        refreshBalance();
        const onBalanceChanged = () => refreshBalance();
        window.addEventListener('billing:balance-changed', onBalanceChanged);
        return () => {
            cancelled = true;
            window.removeEventListener('billing:balance-changed', onBalanceChanged);
        };
    }, [user?.id, activeOrgId]);

    const isActive = (path: string) => {
        if (path.includes('?')) {
            return `${location.pathname}${location.search}` === path;
        }
        if (path === '/list-property') {
            const params = new URLSearchParams(location.search);
            const isBulk = params.get('tab') === 'bulk' || params.get('bulk') === 'true';
            return location.pathname === '/list-property' && !isBulk;
        }
        if (path === '/import-properties') {
            const params = new URLSearchParams(location.search);
            const isBulk = params.get('tab') === 'bulk' || params.get('bulk') === 'true';
            return (
                location.pathname === '/import-properties' ||
                location.pathname === '/import-property' ||
                location.pathname === '/bulk-properties' ||
                location.pathname === '/bulk-property' ||
                (location.pathname === '/list-property' && isBulk)
            );
        }
        return location.pathname === path;
    };
    const isPropertySearchActive =
        location.pathname === PROPERTY_SEARCH_ASSISTANT_PATH ||
        location.pathname.startsWith(`${PROPERTY_SEARCH_ASSISTANT_PATH}/`);

    const goToListProperty = () => navigate('/list-property/new');

    const [isMobileOpen, setIsMobileOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useLayoutEffect(() => {
        const path = location.pathname;
        document.querySelectorAll<HTMLElement>('[data-dashboard-nav]').forEach((root) => {
            const links = root.querySelectorAll<HTMLAnchorElement>('a[href]');
            for (const link of links) {
                let pathname = '';
                try {
                    pathname = new URL(link.href, window.location.origin).pathname;
                } catch {
                    continue;
                }
                if (pathname !== path) continue;
                const linkBox = link.getBoundingClientRect();
                const rootBox = root.getBoundingClientRect();
                if (linkBox.top < rootBox.top || linkBox.bottom > rootBox.bottom) {
                    root.scrollTop += linkBox.top - rootBox.top - 8;
                }
                break;
            }
        });
    }, [location.pathname, isMobile, user?.role]);

    const renderSidebar = (onLinkClick?: () => void) => (
        <div className="flex flex-col h-full bg-card">
            <div className={`px-4 py-4 flex items-center gap-2 ${onLinkClick ? 'pr-12' : ''}`}>
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity flex-1" onClick={onLinkClick}>
                    <div className="h-8 w-8 bg-black rounded-lg flex items-center justify-center">
                        <Building2 className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-bold text-lg">Letmein-ai</span>
                </Link>
            </div>

            <div data-dashboard-nav className="flex-1 px-4 py-2 space-y-2 overflow-y-auto custom-scrollbar">
                <nav className="space-y-1">
                    <Link to="/dashboard" onClick={onLinkClick}>
                        <Button variant={isActive('/dashboard') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                            <LayoutGrid className="mr-3 h-4 w-4" /> {t('sidebar.dashboard')}
                        </Button>
                    </Link>


                    {(activeRole === 'admin' || activeRole === 'super_admin') && (
                        <>
                            <Link to="/admin/users" onClick={onLinkClick}>
                                <Button variant={isActive('/admin/users') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                    <Users className="mr-3 h-4 w-4" /> {t('sidebar.users')}
                                </Button>
                            </Link>
                            <Link to="/organizations" onClick={onLinkClick}>
                                <Button variant={isActive('/organizations') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                    <Building2 className="mr-3 h-4 w-4" /> {t('sidebar.organizations', 'Builders')}
                                </Button>
                            </Link>
                        </>
                    )}

                    {activeRole === 'org_admin' && (
                        <>
                            <Link to="/org/users" onClick={onLinkClick}>
                                <Button variant={isActive('/org/users') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                    <Users className="mr-3 h-4 w-4" /> {t('sidebar.org_users')}
                                </Button>
                            </Link>
                        </>
                    )}

                    <Link to="/messages" onClick={onLinkClick}>
                        <Button variant={isActive('/messages') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                            <MessageSquare className="mr-3 h-4 w-4" /> {t('sidebar.messages')}
                        </Button>
                    </Link>

                    <Button
                        type="button"
                        variant={isPropertySearchActive ? 'secondary' : 'ghost'}
                        className="w-full justify-start font-medium"
                        disabled={searchChecking}
                        onClick={() => {
                            void goToPropertySearch();
                            onLinkClick?.();
                        }}
                    >
                        {searchChecking ? (
                            <Loader2 className="mr-3 h-4 w-4 animate-spin" />
                        ) : (
                            <Search className="mr-3 h-4 w-4" />
                        )}
                        {t('sidebar.property_search', 'Property Search')}
                    </Button>

                    {isOwnerTenantUser ? (
                        <>
                            <div className="h-px bg-border mx-2 my-3" />
                            <div className="px-2 py-1">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">{t('sidebar.tenant', 'Tenant')}</p>
                                <nav className="space-y-1">
                                    <Link to="/favourites" onClick={onLinkClick}>
                                        <Button variant={isActive('/favourites') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Heart className="mr-3 h-4 w-4" /> {t('sidebar.my_favourites')}
                                        </Button>
                                    </Link>
                                    <Link to="/tenants" onClick={onLinkClick}>
                                        <Button variant={isActive('/tenants') && activeRole === 'tenant' ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <UserCheck className="mr-3 h-4 w-4" /> {t('sidebar.my_leases', 'My Leases')}
                                        </Button>
                                    </Link>
                                </nav>
                            </div>

                            <div className="h-px bg-border mx-2 my-3" />
                            <div className="px-2 py-1">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">{t('sidebar.owner', 'Owner')}</p>
                                <nav className="space-y-1">
                                    <Link to="/list-property" onClick={onLinkClick}>
                                        <Button variant={isActive('/list-property') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Building2 className="mr-3 h-4 w-4" /> {t('sidebar.my_properties')}
                                        </Button>
                                    </Link>
                                    <Link to="/import-properties" onClick={onLinkClick}>
                                        <Button variant={isActive('/import-properties') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <FileSpreadsheet className="mr-3 h-4 w-4" /> {t('sidebar.import_properties', 'Import Properties')}
                                        </Button>
                                    </Link>
                                    <Link to="/tenants" onClick={onLinkClick}>
                                        <Button variant={isActive('/tenants') && activeRole !== 'tenant' ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <UserCheck className="mr-3 h-4 w-4" /> {t('sidebar.tenants', 'Tenants & Inquiries')}
                                        </Button>
                                    </Link>
                                    <Link to="/brokerage" onClick={onLinkClick}>
                                        <Button variant={isActive('/brokerage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Users className="mr-3 h-4 w-4" /> {t('sidebar.brokerage_hub')}
                                        </Button>
                                    </Link>
                                    {isMobile && canPostProperty && (
                                        <div className="pt-3 pb-1">
                                            <Button
                                                className="w-full gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold shadow-md shadow-primary/20 hover:scale-[1.01] active:scale-[0.99] transition-all duration-200 rounded-lg h-11"
                                                onClick={() => {
                                                    goToListProperty();
                                                    onLinkClick?.();
                                                }}
                                            >
                                                <Plus className="h-4 w-4" /> {t('property_listing.actions.post_property', 'Post Property')}
                                            </Button>
                                        </div>
                                    )}
                                </nav>
                            </div>

                            <div className="h-px bg-border mx-2 my-3" />
                            <div className="px-2 py-1">
                                <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">{t('sidebar.analytics_reports')}</p>
                                <nav className="space-y-1">
                                    <Link to="/analytics" onClick={onLinkClick}>
                                        <Button variant={isActive('/analytics') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <BarChart3 className="mr-3 h-4 w-4" /> {t('sidebar.analytics')}
                                        </Button>
                                    </Link>
                                    <Link to="/usage" onClick={onLinkClick}>
                                        <Button variant={isActive('/usage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Wallet className="mr-3 h-4 w-4" /> {t('sidebar.usage_billing', 'Usage & Billing')}
                                        </Button>
                                    </Link>
                                </nav>
                            </div>
                        </>
                    ) : (
                        <>
                            {activeRole === 'tenant' ? (
                                <>
                                    <Link to="/favourites" onClick={onLinkClick}>
                                        <Button variant={isActive('/favourites') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Heart className="mr-3 h-4 w-4" /> {t('sidebar.my_favourites')}
                                        </Button>
                                    </Link>
                                    <Link to="/tenants" onClick={onLinkClick}>
                                        <Button variant={isActive('/tenants') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <UserCheck className="mr-3 h-4 w-4" /> {t('sidebar.my_leases', 'My Leases')}
                                        </Button>
                                    </Link>
                                    <Link to="/import-properties" onClick={onLinkClick}>
                                        <Button variant={isActive('/import-properties') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <FileSpreadsheet className="mr-3 h-4 w-4" /> {t('sidebar.import_properties', 'Import Properties')}
                                        </Button>
                                    </Link>

                                    {isMobile && (
                                        <div className="px-1 pt-3">
                                            <Button
                                                className="w-full gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold shadow-md shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-lg h-11"
                                                onClick={() => {
                                                    goToListProperty();
                                                    onLinkClick?.();
                                                }}
                                            >
                                                <Plus className="h-4 w-4" /> {t('property_listing.actions.post_property', 'Post Property')}
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Link to="/list-property" onClick={onLinkClick}>
                                        <Button variant={isActive('/list-property') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <Building2 className="mr-3 h-4 w-4" /> {(activeRole === 'org_admin' || activeRole === 'org_user') ? t('sidebar.org_properties') : t('sidebar.my_properties')}
                                        </Button>
                                    </Link>
                                    <Link to="/import-properties" onClick={onLinkClick}>
                                        <Button variant={isActive('/import-properties') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <FileSpreadsheet className="mr-3 h-4 w-4" /> {t('sidebar.import_properties', 'Import Properties')}
                                        </Button>
                                    </Link>
                                </>
                            )}

                            {isMobile && canPostProperty && activeRole !== 'tenant' && (
                                <div className="px-1 pt-2 pb-1">
                                    <Button
                                        className="w-full gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-bold shadow-md shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 rounded-lg h-11"
                                        onClick={() => {
                                            goToListProperty();
                                            onLinkClick?.();
                                        }}
                                    >
                                        <Plus className="h-4 w-4" /> {t('property_listing.actions.post_property', 'Post Property')}
                                    </Button>
                                </div>
                            )}

                            {(activeRole === 'owner' || activeRole === 'property_manager' || activeRole === 'admin' || activeRole === 'super_admin') && (
                                <Link to="/tenants" onClick={onLinkClick}>
                                    <Button variant={isActive('/tenants') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <UserCheck className="mr-3 h-4 w-4" /> {t('sidebar.tenants', 'Tenants & Inquiries')}
                                    </Button>
                                </Link>
                            )}

                            {(activeRole === 'owner' || activeRole === 'property_manager' || activeRole === 'super_admin' || activeRole === 'org_admin') && (
                                <Link to="/brokerage" onClick={onLinkClick}>
                                    <Button variant={isActive('/brokerage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Users className="mr-3 h-4 w-4" /> {t('sidebar.brokerage_hub')}
                                    </Button>
                                </Link>
                            )}

                            {(activeRole === 'admin' || activeRole === 'super_admin') && (
                                <Link to="/all-properties" onClick={onLinkClick}>
                                    <Button variant={isActive('/all-properties') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Search className="mr-3 h-4 w-4" /> {t('sidebar.all_properties')}
                                    </Button>
                                </Link>
                            )}
                        </>
                    )}
                </nav>

                {!isOwnerTenantUser && (activeRole === 'owner' || activeRole === 'property_manager' || activeRole === 'org_admin' || activeRole === 'admin' || activeRole === 'super_admin') && (
                    <>
                        <div className="h-px bg-border mx-2 my-3" />
                        <div className="px-2 py-1">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">{t('sidebar.analytics_reports')}</p>
                            <nav className="space-y-1">
                                <Link to="/analytics" onClick={onLinkClick}>
                                    <Button variant={isActive('/analytics') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <BarChart3 className="mr-3 h-4 w-4" /> {t('sidebar.analytics')}
                                    </Button>
                                </Link>
                                {(activeRole === 'admin' || activeRole === 'super_admin' || activeRole === 'org_admin') && (
                                    <>
                                        <Link to="/reports" onClick={onLinkClick}>
                                            <Button variant={isActive('/reports') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                                <FileText className="mr-3 h-4 w-4" /> {t('sidebar.reports')}
                                            </Button>
                                        </Link>
                                        <Link to="/admin/activity-logs" onClick={onLinkClick}>
                                            <Button variant={isActive('/admin/activity-logs') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                                <History className="mr-3 h-4 w-4" /> {t('sidebar.activity_logs')}
                                            </Button>
                                        </Link>
                                    </>
                                )}
                            </nav>
                        </div>
                    </>
                )}

                <div className="h-px bg-border mx-2 my-3" />

                {(activeRole === 'super_admin' || activeRole === 'admin') && (
                    <>
                        <div className="px-2 py-1">
                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">{t('sidebar.administration')}</p>
                            <nav className="space-y-1">
                                <RolesDropdown />

                                {!creditsOnlyMode && (
                                    <Link to="/admin/subscriptions" onClick={onLinkClick}>
                                        <Button variant={isActive('/admin/subscriptions') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                            <CreditCard className="mr-3 h-4 w-4" /> {t('sidebar.all_subscriptions')}
                                        </Button>
                                    </Link>
                                )}
                                <Link to="/admin/transactions" onClick={onLinkClick}>
                                    <Button variant={isActive('/admin/transactions') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <CreditCard className="mr-3 h-4 w-4" /> {t('sidebar.transactions')}
                                    </Button>
                                </Link>
                                <Link to="/admin/api-usage" onClick={onLinkClick}>
                                    <Button variant={isActive('/admin/api-usage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Activity className="mr-3 h-4 w-4" /> {t('sidebar.api_usage')}
                                    </Button>
                                </Link>
                                <Link to="/admin/reviews" onClick={onLinkClick}>
                                    <Button variant={isActive('/admin/reviews') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Star className="mr-3 h-4 w-4" /> {t('sidebar.reviews', 'Reviews')}
                                    </Button>
                                </Link>
                                <Link to="/admin/feedback" onClick={onLinkClick}>
                                    <Button variant={isActive('/admin/feedback') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <MessageSquareHeart className="mr-3 h-4 w-4" /> {t('sidebar.feedback', 'Feedback')}
                                    </Button>
                                </Link>
                                <Link to="/admin/token-usage" onClick={onLinkClick}>
                                    <Button variant={isActive('/admin/token-usage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Coins className="mr-3 h-4 w-4" /> {t('sidebar.token_usage', 'Token usage')}
                                    </Button>
                                </Link>
                                {activeRole === 'super_admin' && (
                                    <>
                                        <Link to="/admin/revenue" onClick={onLinkClick}>
                                            <Button variant={isActive('/admin/revenue') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                                <DollarSign className="mr-3 h-4 w-4" /> {t('sidebar.revenue', 'Revenue')}
                                            </Button>
                                        </Link>
                                        <Link to="/admin/redis-insight" onClick={onLinkClick}>
                                            <Button variant={isActive('/admin/redis-insight') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                                <Database className="mr-3 h-4 w-4" /> {t('sidebar.redis_insight', 'Redis Insight')}
                                            </Button>
                                        </Link>
                                        <Link to="/admin/google-maps" onClick={onLinkClick}>
                                            <Button variant={isActive('/admin/google-maps') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                                <MapPin className="mr-3 h-4 w-4" /> {t('sidebar.google_maps_usage', 'Maps & Places cost')}
                                            </Button>
                                        </Link>
                                    </>
                                )}
                                <Link to="/settings" onClick={onLinkClick}>
                                    <Button variant={isActive('/settings') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                        <Settings className="mr-3 h-4 w-4" /> {t('sidebar.settings')}
                                    </Button>
                                </Link>
                            </nav>
                        </div>
                        <div className="h-px bg-border mx-2 my-3" />
                    </>
                )}


                <nav className="space-y-1">
                    {!creditsOnlyMode && (
                        <Link to={user?.role === 'super_admin' ? "/admin/plans" : "/plans"} onClick={onLinkClick}>
                            <Button variant={isActive(user?.role === 'super_admin' ? '/admin/plans' : '/plans') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                <CreditCard className="mr-3 h-4 w-4" /> {user?.role === 'super_admin' ? t('sidebar.manage_plans') : (user?.role === 'org_admin' || user?.role === 'org_user') ? t('sidebar.subscription') : t('sidebar.my_subscription')}
                            </Button>
                        </Link>
                    )}
                    {activeRole !== 'tenant' && !isOwnerTenantUser && (
                        <>
                            <Link to="/usage" onClick={onLinkClick}>
                                <Button variant={isActive('/usage') ? 'secondary' : 'ghost'} className="w-full justify-start font-medium">
                                    <Wallet className="mr-3 h-4 w-4" /> {t('sidebar.usage_billing', 'Usage & Billing')}
                                </Button>
                            </Link>
                        </>
                    )}
                </nav>
            </div>

            {user && (
                <div className="p-4 border-t bg-muted/30">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-9 w-9 border-2 border-background">
                            <AvatarImage src={getUserProfileImageUrl(user)} />
                            <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">
                                {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase()}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col min-w-0">
                            <span className="text-sm font-semibold truncate leading-none mb-1">
                                {user.first_name} {user.last_name}
                            </span>
                            <span className="text-[10px] font-medium text-muted-foreground truncate max-w-[150px]">
                                {user.email}
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-background flex">
            {/* Sidebar */}
            {showSidebar && (
                <aside className="w-64 border-r bg-card hidden lg:flex flex-col sticky top-0 h-screen z-50">
                    {renderSidebar()}
                </aside>
            )}

            {/* Main Content */}
            <div className="flex-1 flex flex-col min-w-0 relative z-10 bg-muted/10 overflow-x-hidden">
                {/* Header */}
                <header className="h-16 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex items-center px-2 sm:px-4 lg:px-6 justify-between gap-1 sm:gap-2 sticky top-0 z-40 min-w-0">
                    <div className={cn("flex items-center gap-1 sm:gap-2 min-w-0", showSidebar && "lg:hidden")}>
                        {showSidebar && (
                            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
                                <SheetTrigger asChild>
                                    <Button variant="ghost" size="icon" className="-ml-2">
                                        <Menu className="h-6 w-6" />
                                    </Button>
                                </SheetTrigger>
                                <SheetContent side="left" className="p-0 w-64">
                                    {renderSidebar(() => setIsMobileOpen(false))}
                                </SheetContent>
                            </Sheet>
                        )}
                        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
                            <div className="h-8 w-8 bg-black rounded-lg flex items-center justify-center">
                                <Building2 className="h-5 w-5 text-white" />
                            </div>
                            <span className="font-bold hidden sm:inline">Letmein-ai</span>
                        </Link>
                    </div>

                    {/* Spacer for desktop to push content to right */}
                    <div className="hidden lg:flex items-center flex-1 h-full">
                        {user && (activeRole === 'org_admin' || activeRole === 'org_user') && user.organizations?.[0] && (
                            <div className="flex flex-col items-start px-6 border-r border-border h-2/3 justify-center">
                                <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest leading-none mb-1">{t('common.organization')}</span>
                                <div className="flex items-center gap-1.5 text-primary">
                                    <Building2 className="h-4 w-4" />
                                    <span className="text-sm font-bold truncate max-w-[250px]">
                                        {user.organizations[0].name}
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Header actions: Post Property / Wallet → Theme → Bell → Profile */}
                    <div className="flex items-center gap-1.5 sm:gap-3 md:gap-4 shrink-0 min-w-0">
                        {canPostProperty && !isMobile && (
                            <Button
                                type="button"
                                size="sm"
                                className="gap-2 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-primary-foreground font-semibold shadow-md shadow-primary/20 hover:scale-105 transition-all duration-200 rounded-lg"
                                onClick={goToListProperty}
                            >
                                <Home className="h-4 w-4" />
                                {t('property_listing.actions.post_property', 'Post Property')}
                            </Button>
                        )}
                        {user ? (
                            <>
                                <Link to="/usage" className="flex items-center gap-1 px-2 sm:px-2.5 py-1.5 rounded-lg bg-muted/80 hover:bg-muted text-xs sm:text-sm font-medium tabular-nums shrink-0">
                                    <Coins className="h-4 w-4 text-primary" />
                                    {balanceLoading ? (
                                        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                                    ) : (
                                        <span>{formatWalletBalanceCompact(balance)}</span>
                                    )}
                                </Link>

                                <ThemeToggle />

                                <NotificationBell />

                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Avatar className="cursor-pointer hover:opacity-80 transition-opacity">
                                            <AvatarImage src={getUserProfileImageUrl(user)} />
                                            <AvatarFallback className="bg-muted font-medium">
                                                {`${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase() || 'U'}
                                            </AvatarFallback>
                                        </Avatar>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-[min(100vw-2rem,14rem)] p-2">
                                        <DropdownMenuLabel className="font-normal px-3 py-2">
                                            <div className="flex flex-col gap-1.5">
                                                <p className="text-sm font-medium leading-snug break-words">{user?.first_name} {user?.last_name}</p>
                                                <p className="text-xs leading-snug text-muted-foreground break-all">{user?.email}</p>
                                            </div>
                                        </DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem asChild className="px-3 py-2.5 cursor-pointer">
                                            <Link to="/account" className="cursor-pointer flex w-full items-center gap-2">
                                                <User className="h-4 w-4 shrink-0" />
                                                <span className="truncate">{t('sidebar.profile')}</span>
                                            </Link>
                                        </DropdownMenuItem>
                                        {(activeRole === 'admin' || activeRole === 'super_admin') && (
                                            <DropdownMenuItem asChild className="px-3 py-2.5 cursor-pointer">
                                                <Link to="/settings" className="cursor-pointer flex w-full items-center gap-2">
                                                    <Settings className="h-4 w-4 shrink-0" />
                                                    <span className="truncate">{t('sidebar.settings')}</span>
                                                </Link>
                                            </DropdownMenuItem>
                                        )}
                                        <DropdownMenuSeparator />
                                        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600 focus:text-red-600 px-3 py-2.5 gap-2">
                                            <LogOut className="h-4 w-4 shrink-0" />
                                            {t('sidebar.logout')}
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </>
                        ) : (
                            <div className="flex items-center gap-2">
                                <ThemeToggle />
                                <Button variant="ghost" size="sm" className="font-semibold" asChild>
                                    <Link to="/login" state={{ from: location.pathname + location.search }}>
                                        {t('landing_page.nav_signin', 'Sign In')}
                                    </Link>
                                </Button>
                                <Button size="sm" className="font-semibold bg-primary text-primary-foreground hover:bg-primary/90" asChild>
                                    <Link to="/register" state={{ from: location.pathname + location.search }}>
                                        {t('landing_page.nav_get_started', 'Get Started')}
                                    </Link>
                                </Button>
                            </div>
                        )}
                    </div>
                </header>

                <ImpersonationBanner />

                <main data-scroll-container className="flex-1 overflow-auto">
                    {children}
                </main>
            </div>

            <Suspense fallback={null}>
                <SearchResumeDialog
                    open={showResumeDialog}
                    search={pendingSearch}
                    onContinue={continueSearch}
                    onStartNew={startNewSearch}
                />
            </Suspense>
        </div>
    )
}
